<template>
  <div
    v-resize="onResize"
    class="mx-8"
  >
    <invite-user-modal
      v-model="modalOpen"
      :user-full-name="newUserFullName"
      @edit-user-permissions="onEditPermissions"
      @add-another-user="onAddAnotherUser"
      @return-to-org="onReturnToOrg"
    />
    <v-card class="px-8 py-5">
      <h1 class="mb-5">
        Invite User
      </h1>
      <v-row style="padding: 0 14px">
        <v-form
          id="add-user-org-form"
          ref="form"
          v-model="isValid"
          class="d-flex justify-space-between"
        >
          <div class="add-form-section">
            <h4 class="mb-3 d-flex align-center">
              <v-icon
                class="mr-2"
                color="#535353"
              >
                $faHomeLg
              </v-icon>
              User Details
            </h4>
            <v-text-field
              v-model.trim="firstName"
              class="add-user-field mb-1 mt-3"
              filled
              dense
              label="First Name"
              :rules="rules.firstName"
            />
            <v-text-field
              v-model.trim="lastName"
              class="add-user-field mb-1"
              filled
              dense
              label="Last Name"
              :rules="rules.lastName"
            />
            <v-text-field
              v-model.trim="email"
              class="add-user-field mb-1"
              filled
              dense
              label="Email Address"
              :rules="rules.email"
            />
            <vue-tel-input-vuetify
              :value="phone"
              filled
              max-height="52"
              label="Phone Number"
              :error-messages="phoneErrors"
              @input="handlePhoneInput"
              @blur="validatePhone"
              @country-changed="updateCountry"
            />
          </div>
          <div class="add-form-section">
            <h4 class="mb-3 d-flex align-center">
              <v-icon
                class="mr-2"
                color="#535353"
              >
                $faUserTag
              </v-icon>Organization Role
            </h4>
            <v-radio-group
              v-model="userOrg.role"
              :rules="rules.role"
              class="pt-0 mt-3"
            >
              <v-card
                v-for="item of radios"
                :key="item.ID"
                :color="userOrg.role === item.value ? 'primary' : '#F9F7F3'"
                class="radio-container mt-0 px-5 py-4 mb-7"
                elevation="3"
              >
                <v-radio
                  class="radio"
                  :color="userOrg.role === item.value ? '#F0F4F6' : 'primary'"
                  :value="item.value"
                  on-icon="$faCheckCircle"
                  off-icon="$faCircleRegular"
                >
                  <template v-slot:label>
                    <div :class="userOrg.role === item.value ? 'active' : 'inactive'">
                      {{ item.label }}<br><span style="line-height: 18px">{{ item.text }}</span>
                    </div>
                  </template>
                </v-radio>
              </v-card>
            </v-radio-group>
            <div class="d-flex justify-end">
              <v-btn
                color="secondary"
                @click="handleCancel"
              >
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!isValid || isLoading"
                :loading="isLoading"
                @click="handleSubmit"
              >
                Invite
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  import orgDetailsRouterMixin from '@/helpers/org-details-router-mixin'
  import InviteUserModal from '@/components/orgs/InviteUserModal.vue'
  import validationAxios from '@/helpers/validation-client'

  export default {
    name: 'OrganizationAddUser',
    components: { InviteUserModal },
    mixins: [orgDetailsRouterMixin],
    data () {
      return {
        modalOpen: false,
        newUserId: '',
        newUserFullName: '',
        isValid: false,
        overflowed: false,
        iso2: '',
        phoneValid: '',
        phoneValidated: '',
        phoneE164: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'external',
        rules: {
          firstName: [v => !!(v.trim()) || 'First name is required'],
          lastName: [v => !!(v.trim()) || 'Last name is required'],
          email: [v => !!(v.trim()) || 'E-mail is required', v => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
          role: [v => !!v || 'Role is required'],
        },
        radios: [
          { label: 'Administrator', value: 'Administrator', text: 'Administrators can view and edit all properties and manage users.' },
          { label: ' Manager', value: 'Manager', text: 'View or edit properties based on permissions. Cannot manage users.' },
          { label: 'Watcher', value: 'Watcher', text: 'View-only role. Cannot edit properties or users.' },
        ],
        orgName: '...',
        userOrg: {
          id: '',
          name: '',
          role: '',
        },
        isLoading: false,
      }
    },
    computed: {
      orgId () {
        return this.$route.params.id
      },
      phoneErrors () {
        if (this.phone) {
          if (!this.iso2) return 'Please select a country'
          if (this.phoneValidated && !this.phoneValid) return 'Invalid Phone Number Format'
          if (this.phoneValidated && !this.phoneIsMobile) return 'Please enter a mobile number'
        }
        return ''
      },
    },
    created () {
      // Get the organization data
      this.$store.dispatch('Organizations/fetchOrgForNewUser', this.orgId)
        .then((res) => {
          const { id, name } = res.data.data
          this.orgName = name
          this.userOrg.id = id
          this.userOrg.name = name
        })
    },
    methods: {
      onResize () {
        this.overflowed = (window.innerWidth < 1578)
      },
      updateCountry ({ iso2 }) {
        this.iso2 = iso2
        this.validatePhone()
      },
      handlePhoneInput (phone, { number }) {
        this.phoneValidated = false
        this.phone = phone
        if (number.e164) {
          this.phoneE164 = number.e164
        } else this.phoneE164 = ''
      },
      async validatePhone () {
        if (!this.iso2 || !this.phoneE164) return
        try {
          const { data } = await validationAxios.post(`${process.env.VUE_APP_VALIDATOR_URL}/api/v1/validation/phone`, { phone: this.phoneE164 })
          this.phoneValid = true
          this.phoneIsMobile = data.isMobile
        } catch (err) {
          this.phoneValid = false
        } finally {
          this.phoneValidated = true
        }
      },
      handleCancel () {
        this.$router.push(`/organizations/${this.orgId}`)
      },
      onEditPermissions () {
        this.$router.push({ name: 'Admin User', query: { edit: true }, params: { orgId: `${this.orgId}`, id: `${this.newUserId}` } })
      },
      onAddAnotherUser () {
        this.modalOpen = false
        this.$refs.form.reset()
      },
      onReturnToOrg () {
        this.$router.push(`/organizations/${this.orgId}`)
      },
      handleSubmit () {
        const payload = {
          orgUser: {
            orgId: Number.parseInt(this.$route.params.id),
            orgRoleName: this.userOrg.role,
          },
          user: {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email.trim(),
            phone: this.phone,
            role: 'external',
          },
        }
        if (this.isValid) {
          this.isLoading = true
          this.$store.dispatch('Organizations/inviteUser', payload)
            .then((res) => {
              if (res.data && res.data.data && res.data.data.id) {
                const { id } = res.data.data
                this.newUserId = id
                this.newUserFullName = `${this.firstName || ''} ${this.lastName || ''}`
                this.modalOpen = true
              }
            })
            .finally(() => {
              this.isLoading = false
            })
        }
      },
    },
  }
</script>

<style lang="scss">
#add-user-org-form {
  width: 100%;
  .add-form-section {
    width: 49%;
    .radio-divider {
      height: 44px;
      min-width: 1px;
      background-color: #E1EBEE;
    }
    .add-user-field label {
      color:#535353;
    }
  }
  .radio-container {
    height: auto;
    width:100%;
    .radio {
      .v-input--selection-controls__ripple {
        left: -8px;
        top:calc(50% - 20px );
        height:25px;
        width:25px;
      }
      label {
        font-weight: 600;
        font-size: 16px;
        span {
          line-height: 18px;
          font-size: 14px;
          font-weight: 500;
        }
      }
      .active { color: #fff; }
      .inactive { color: #535353; }
    }
  }
}
.abbreviate {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.country-code .v-input__slot {
  padding: 0 !important;
}
</style>
